import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { removeProjectIdFromTableName } from '@/lib/supabase/utils'
import {
  EllipsisVerticalIcon,
  Loader2,
  Pencil,
  PencilRulerIcon,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react'
import DOMPurify from 'dompurify'
import { useLocale, useTranslations } from 'next-intl'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { useDatabaseContext } from '../../context'
import { ExportCsv } from '../export-csv'
import { DataTable } from '../table'
import { popModal, pushModal } from '@/components/modals'
import { useConfirm } from '@/hooks/use-confirm'
import { nanoid } from 'nanoid'
import type { ProjectMessage, TableFieldDefinition } from '@/lib/types'
import { useProjectStore } from '@/hooks/use-project-store'
import { useAICoding } from '@/hooks/use-ai-coding'
import sendToBothAnalytics from '@/lib/mixpanel/sendToBothAnalytics'
import { MIXPANEL_CLIENT_EVENT_NAMES } from '@/lib/mixpanel/constant'
import { useCurrentUser } from '@/hooks/use-current-user'
import {
  HoverCard,
  HoverCardPortal,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import DatabaseBatchActions from '../../DatabaseBatchActions'
import { Fields } from '../../Form/UpsertTableColumn'
import { AUTO_GENERATED_FIELDS } from '../../shared'
import { cn, fetcher } from '@/lib/utils'
import dayjs from 'dayjs'
import { Spin } from '@/components/ui/spin'
import { useUserCoin } from '@/hooks/use-user-coin'
import { PaginationClass } from '@/shared'
import { ImageThumbnail, isImageUrl } from '@/components/ui/image-preview-modal'
import { PaginationView } from '@/components/common/pagination-view'
import type { Row } from '@tanstack/react-table'
interface NormalDataTableProps {
  onDataChange?: () => void
  setIsDataChange: (isDataChange: boolean) => void
}

export const NormalDataTable = ({
  onDataChange,
  setIsDataChange,
}: NormalDataTableProps) => {
  const {
    tableName,
    normalTableData,
    normalTablePagination,
    setNormalTablePagination,
    project,
    refetchTables,
    selectedTableColumn,
  } = useDatabaseContext()

  const { addMessage, api__appendMessage } = useProjectStore()
  const { aiCoding } = useAICoding()
  const { data: user } = useCurrentUser()
  const { checkOutOfCredit } = useUserCoin()

  const locale = useLocale()

  const t = useTranslations('table_manager')
  const tdb = useTranslations('database')
  const realTableName = removeProjectIdFromTableName(tableName, project!)
  const [isLoading, setIsLoading] = useState(false)
  const [searchValue, setSearchValue] = useState<string>('')
  const [searchColumns, setSearchColumns] = useState<any[]>([])
  const [searchColumn, setSearchColumn] = useState<any>(null)
  const [dataSource, setDataSource] = useState<any[]>(normalTableData)
  const [enableAdvanceSetting, setEnableAdvanceSetting] =
    useState<boolean>(true)
  const [editingRecord, setEditingRecord] = useState<any>(null)
  const [recordToDelete, setRecordToDelete] = useState<any>(null)
  const [selectedRowsIndex, setSelectedRowsIndex] = useState<Set<any>>(
    new Set(),
  )
  const { confirm } = useConfirm()

  const projectId = project?._id.toString()

  useEffect(() => {
    const table = project?.table_columns?.find(
      (item) => item.db_table_name === tableName,
    )
    const columns = table?.field_list?.filter((tableCol) =>
      ['text', 'rich_text', 'media_url', 'web_url'].includes(tableCol?.type),
    )
    if (columns && columns.length > 0) {
      setSearchColumn(columns[0].db_name || null)
      setSearchColumns(columns)
    }
  }, [project, tableName])

  useEffect(() => {
    setDataSource(normalTableData)
  }, [normalTableData])

  // Helper function to create default values for new records
  const createEmptyRecord = () => {
    if (!project?.table_columns || !tableName) return {}

    const currentTableColumns = project.table_columns.find(
      (tableCol) => tableCol.db_table_name === tableName,
    )

    if (!currentTableColumns) return {}

    const emptyRecord: Record<string, any> = {}

    for (const field of currentTableColumns.field_list) {
      // Skip auto-generated fields
      if (field.type === 'id') {
        continue
      }
      // Set default values based on field type
      switch (field.type) {
        case 'checkbox':
          emptyRecord[field.db_name] = false
          break
        case 'tag':
          if (field.tag_type === 'multiple') {
            emptyRecord[field.db_name] = []
          }
          break
        default:
          emptyRecord[field.db_name] = undefined
      }
    }

    return emptyRecord
  }

  const handleDelete = async (id: string) => {
    try {
      setIsLoading(true)
      const response = await fetch(
        `/api/me/project/${projectId}/table/${tableName}/crud?recordId=${id}`,
        {
          method: 'DELETE',
        },
      )
      if (!response.ok) throw new Error('Failed to delete record')
      toast.success('Record deleted successfully')
      setRecordToDelete(null)
      onDataChange?.()
      setIsDataChange(true)
    } catch (error) {
      toast.error('Failed to delete record')
    } finally {
      setIsLoading(false)
    }
  }

  const handleBulkDelete = async () => {
    try {
      setIsLoading(true)

      // 通过 selectedRowsIndex 换 row id
      const selectedRowsIds = Array.from(selectedRowsIndex)
        .map((index) => normalTableData[index]! as unknown as { id: string })
        .map((row) => row.id)

      const response = await fetch(
        `/api/me/project/${projectId}/table/${tableName}/crud`,
        {
          method: 'DELETE',
          body: JSON.stringify({
            recordIds: selectedRowsIds,
          }),
        },
      )

      if (!response.ok) throw new Error('Failed to delete selected rows')
      toast.success('Selected rows deleted successfully, wait for refresh')
      onDataChange?.()
    } catch (error) {
      toast.error('Failed to delete selected rows')
    } finally {
      setIsLoading(false)
      setSelectedRowsIndex(new Set([]))
      setIsDataChange(true)
    }
  }

  // 旧版本升级
  const aiFixDatabase = async () => {
    const addedUserMessage = {
      role: 'user',
      content: `Please generate the corresponding "xxx.json" files for the migration files that do not have them yet.`,
    } as ProjectMessage
    const newUserMessage = await api__appendMessage(addedUserMessage)
    if (!newUserMessage) {
      console.error('Failed to save fix database message')
      return
    }
    addMessage(newUserMessage)
    aiCoding({ addedUserMessage: newUserMessage })
  }

  const databaseMigrateWarning = () => {
    confirm({
      description: 'Table column definition not found.',
      onOk: () => {
        popModal()
        aiFixDatabase()
      },
    })
  }

  const bulkDeleteConfirm = () => {
    confirm({
      title: 'Delete Rows?',
      description:
        'Are you sure you want to delete the selected rows? It will be removed from your data.',
      okText: 'Delete',
      onOk: () => {
        handleBulkDelete()
      },
    })
  }

  const handleEditingRecord = (record: any) => {
    setEditingRecord(record)
    if (selectedTableColumn) {
      pushModal('DatabaseFormModal', {
        table: selectedTableColumn,
        record,
        mode: 'EDIT',
        onClose: () => {
          popModal()
          setEditingRecord(null)
          onDataChange?.()
          setIsDataChange(true)
        },
      })
      return
    }
    databaseMigrateWarning()
  }

  const handleAddRecord = () => {
    sendToBothAnalytics(
      MIXPANEL_CLIENT_EVENT_NAMES.CLICK_ADD_NEW_RECORD_ADMIN,
      {
        table_name: realTableName,
        project_id: project?._id?.toString(),
      },
      user?._id?.toString(),
    )

    setEditingRecord(createEmptyRecord())
    if (selectedTableColumn) {
      const CreateTableColumn = {
        ...selectedTableColumn,
        field_list: selectedTableColumn.field_list.filter(
          (d) => !AUTO_GENERATED_FIELDS.includes(d.db_name),
        ),
      }
      pushModal('DatabaseFormModal', {
        table: CreateTableColumn,
        record: editingRecord,
        mode: 'CREATE',
        onClose: () => {
          popModal()
          onDataChange?.()
          setIsDataChange(true)
        },
      })
      return
    }
    databaseMigrateWarning()
  }

  const handleBatchOk = () => {
    onDataChange?.()
  }

  const handleAlterTable = async (payload: {
    type: 'add' | 'drop' | 'alter'
    columnName: string
    oldColumnName?: string
    dataType?: string
    oldDataType?: string
  }) => {
    const isOutOfCredit = await checkOutOfCredit()
    if (isOutOfCredit) {
      return
    }

    let prompt = ''
    switch (payload.type) {
      case 'add':
        prompt = `Add a new column to the table ${tableName} with the following name: ${payload.columnName} and type: ${payload.dataType}`
        break
      case 'drop':
        prompt = `Drop the column ${payload.columnName} from the table ${tableName} if it exists`
        break
      case 'alter':
        // Update column name
        if (
          payload.oldColumnName !== payload.columnName &&
          payload.dataType === payload.oldDataType
        ) {
          prompt = `Rename the column ${payload.oldColumnName} in the table ${tableName} to ${payload.columnName}`
        } else if (
          // Update column type
          payload.oldColumnName === payload.columnName &&
          payload.dataType !== payload.oldDataType
        ) {
          prompt = `Alter the column ${payload.columnName} type in the table ${tableName} to from ${payload.oldDataType} to ${payload.dataType}`
        } else if (
          // Update column name and type
          payload.oldColumnName !== payload.columnName &&
          payload.dataType !== payload.oldDataType
        ) {
          prompt = `Alter the column ${payload.columnName} type in the table ${tableName} to from ${payload.oldDataType} to ${payload.dataType} and rename column name from ${payload.oldColumnName} to ${payload.columnName}`
        } else {
          // update nothing, do nothing
        }
        break
      default:
        break
    }
    const addedUserMessage = {
      role: 'user',
      content: prompt,
    } as ProjectMessage
    const newUserMessage = await api__appendMessage(addedUserMessage)
    if (!newUserMessage) {
      console.error('Failed to save alter table message')
      return
    }
    addMessage(newUserMessage)
    aiCoding({ addedUserMessage: newUserMessage, codingType: 'database_alter' })
    popModal()
  }

  const handleAddNewColumn = () => {
    sendToBothAnalytics(
      MIXPANEL_CLIENT_EVENT_NAMES.CLICK_ADD_COLUMN_ADMIN,
      {
        table_name: realTableName,
        project_id: project?._id?.toString(),
      },
      user?._id?.toString(),
    )

    pushModal('UpsertTableColumnModal', {
      onClose: () => popModal(),
      record: null,
      onUpsert: (data: any) => {
        handleAlterTable(data)
        setTimeout(() => {
          popModal('UpsertTableColumnModal')
        }, 1000)
      },
    })
  }

  const handleEditColumn = (column: any) => {
    pushModal('UpsertTableColumnModal', {
      onClose: () => popModal(),
      record: {
        fieldName: column.db_name,
        type: column.type || 'text',
      },
      onUpsert: (data: any) => {
        handleAlterTable(data)
        setTimeout(() => {
          popModal('UpsertTableColumnModal')
        }, 1000)
      },
    })
  }

  const handleDeleteColumn = (column: any) => {
    pushModal('DeleteTableColumnModal', {
      onClose: () => popModal(),
      fieldName: column.db_name,
      onDelete: () => {
        handleAlterTable({
          type: 'drop',
          columnName: column.db_name,
        })
        setTimeout(() => {
          popModal('DeleteTableColumnModal')
        }, 1000)
      },
    })
  }

  const handleDeleteRecord = (record: any) => {
    setRecordToDelete(record)
  }

  const handleSearch = async () => {
    if (searchValue.trim()) {
      try {
        setIsLoading(true)
        const response = await fetch(
          `/api/me/project/${projectId}/table/${tableName}?search=${searchValue}&columnName=${searchColumn}`,
          {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
          },
        )
        if (!response.ok) throw new Error('Failed to search')
        const data = await response.json()
        setDataSource(data?.data ? data?.data : [])
      } catch (error) {
        toast.error('Failed to search')
      } finally {
        setIsLoading(false)
      }
    } else {
      setDataSource(normalTableData)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const handleClear = () => {
    setSearchValue('')
    setDataSource(normalTableData)
  }

  const handleRowClick = (event: any, row: Row<any>) => {
    if (event.target.closest('button') || event.target.closest('a')) {
      event.preventDefault()
      return false
    }
    handleEditingRecord(row.original)
  }

  const columnsWithExtra = project?.table_columns
    ?.find((tableCol) => tableCol.db_table_name === tableName)
    ?.field_list.map((column) => {
      return {
        id: column.db_name,
        accessorKey: column.db_name,
        size: ['id'].includes(column.type as string) ? 80 : 200,
        header: () => {
          return (
            <div className="flex items-center justify-between gap-2">
              <div
                className={cn('text-center', {
                  'min-w-80': ['rich_text'].includes(column.type),
                  'min-w-48': ['media_url', 'web_url'].includes(column.type),
                  'min-w-24': [
                    'boolean',
                    'integer',
                    'checkbox',
                    'number',
                    'timestamp',
                    'tag',
                    'text',
                  ].includes(column.type),
                })}
              >
                <div className="flex items-center justify-center gap-1">
                  {Fields[column.type]?.icon}
                  {column.ui_name}
                </div>
              </div>
              <div>
                {enableAdvanceSetting && (
                  <HoverCard openDelay={400} closeDelay={400}>
                    <HoverCardTrigger>
                      <EllipsisVerticalIcon className="h-4 w-4 cursor-pointer" />
                    </HoverCardTrigger>
                    <HoverCardPortal>
                      <HoverCardContent className="bg-modal-gradient rounded-lg border-modal-border-color relative">
                        <div className="flex flex-col gap-2 space-y-2 p-2 rounded-lg">
                          <button
                            className="flex items-start gap-2 cursor-pointer"
                            onClick={() => handleEditColumn(column)}
                            type="button"
                          >
                            <Pencil className="h-4 w-4" />
                            <div>Edit</div>
                          </button>
                          <button
                            className="flex items-start gap-2 text-red-500 cursor-pointer"
                            onClick={() => handleDeleteColumn(column)}
                            type="button"
                          >
                            <Trash2 className="h-4 w-4" />
                            <div>Delete</div>
                          </button>
                        </div>
                      </HoverCardContent>
                    </HoverCardPortal>
                  </HoverCard>
                )}
              </div>
            </div>
          )
        },
        cell: ({ row }: any) => {
          const value = row.original[column.db_name]
          switch (column.type) {
            case 'rich_text':
              return (
                <div>{DOMPurify.sanitize(value, { ALLOWED_TAGS: [] })}</div>
              )
            case 'timestamp':
              return (
                <div>
                  {value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : ''}
                </div>
              )
            case 'text':
              if (typeof value === 'string') {
                // Check if the text value is an image URL
                if (isImageUrl(value)) {
                  return (
                    <ImageThumbnail
                      key={value}
                      src={value}
                      alt="Database image"
                    />
                  )
                }
                return <div>{value}</div>
              }
              return <div>{JSON.stringify(value)}</div>
            case 'media_url':
            case 'web_url':
              // These column types are specifically for URLs, likely images
              if (typeof value === 'string' && value.trim()) {
                if (isImageUrl(value)) {
                  return (
                    <ImageThumbnail
                      key={value}
                      src={value}
                      alt="Database image"
                    />
                  )
                }
                // If not an image URL, show as a clickable link
                return (
                  <a
                    href={value}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 underline truncate block max-w-[200px]"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {value}
                  </a>
                )
              }
              return <div>{value}</div>
            default:
              // Check if any value is an image URL
              if (typeof value === 'string' && isImageUrl(value)) {
                return <ImageThumbnail src={value} alt="Database image" />
              }
              return <div>{value}</div>
          }
        },
      }
    })

  const columns = [
    {
      id: 'select',
      // 固定列：
      pin: 'left',
      header: ({ table }: any) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
          className="mr-4"
        />
      ),
      cell: ({ row }: any) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="mr-4"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    ...(columnsWithExtra || []),
    ...(enableAdvanceSetting
      ? [
          {
            id: 'add_new_column',
            header: () => (
              <div className="min-w-40 text-center">
                <button
                  type="button"
                  onClick={handleAddNewColumn}
                  className="flex items-center gap-2 py-3"
                >
                  <Plus className="h-4 w-4" /> {t('add_new_column')}
                </button>
              </div>
            ),
          },
        ]
      : []),
    {
      id: 'actions',
      pin: 'right',
      header: () => (
        <div className="min-w-40 text-center flex items-center justify-center gap-1">
          <PencilRulerIcon className="h-4 w-4" />
          <div className="text-white/60">Actions</div>
        </div>
      ),
      cell: ({ row }: any) => {
        const record = row.original
        return (
          <div className="flex gap-2 justify-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleEditingRecord(record)}
              disabled={isLoading}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDeleteRecord(record)}
              disabled={isLoading}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )
      },
    },
  ]

  const [csvTemplateData, setCsvTemplateData] = useState<
    Record<string, any>[] | undefined
  >(undefined)

  const mockDataRequest = useCallback(
    async (fields: TableFieldDefinition[]) => {
      const response = await fetcher(
        `/api/me/project/${projectId}/table/${tableName}/mock`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fields,
            projectName: project?.project_name,
            locale,
          }),
        },
      )

      return response.data
    },
    [projectId, tableName, locale],
  )

  const onPageChange = useCallback(
    (page: number) => {
      setNormalTablePagination((prev) => ({
        ...prev,
        [tableName]: new PaginationClass(
          page,
          prev[tableName].perPage,
          prev[tableName].total,
        ),
      }))
    },
    [setNormalTablePagination, tableName],
  )

  useEffect(() => {
    const computeTemplateData = async () => {
      const fields = selectedTableColumn?.field_list.filter(
        (d) => !AUTO_GENERATED_FIELDS.includes(d.db_name),
      )

      if (!fields || fields.length === 0) {
        setCsvTemplateData(undefined)
        return
      }

      // 如果无法获取到真实数据，通过 ai 生成 mock 数据
      try {
        const mockData = await mockDataRequest(fields)
        setCsvTemplateData(mockData as unknown as Record<string, any>[])
      } catch (err: any) {
        console.error('csv mockDataError: ', err)
        setCsvTemplateData(undefined)
      }
    }

    computeTemplateData()
  }, [selectedTableColumn, mockDataRequest])

  const getTopView = () => {
    return (
      <div className="flex mb-[20px] 2xl:mb-[32px] items-center mr-4 sticky top-0 z-10 bg-muted">
        <div className="text-white text-[32px] font-semibold mr-[32px]">
          {realTableName}
        </div>
        {searchColumns.length > 0 && (
          <div className="flex items-center mr-[20px] space-x-2">
            <Select
              value={searchColumn}
              onValueChange={(column) => setSearchColumn(column)}
            >
              <SelectTrigger className="focus:outline-none rounded-lg w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="focus:outline-none bg-modal-gradient">
                {searchColumns.map((column) => (
                  <SelectItem key={column.db_name} value={column.db_name}>
                    {column.ui_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative ml-8">
              <input
                className="w-80 h-10 pl-3 pr-20 py-2.5 rounded-lg bg-new_ui-6 border-transparent appearance-none outline-none focus-visible:ring-1 focus-visible:ring-[#3b3b3b] focus-visible:border-transparent"
                placeholder="search..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                {searchValue && (
                  <X
                    className="h-4 w-4 text-white/60 cursor-pointer hover:text-white"
                    onClick={handleClear}
                  />
                )}
                <Search
                  className="h-4 w-4 text-white/60 cursor-pointer hover:text-white"
                  onClick={handleSearch}
                />
              </div>
            </div>
          </div>
        )}
        <div className="flex items-center gap-x-[4px] cursor-pointer">
          <ExportCsv />
        </div>
        {/* <div className="flex gap-1 w-6 h-6 items-center justify-center">
          <HoverCard openDelay={0} closeDelay={0}>
            <HoverCardTrigger>
              <EllipsisVerticalIcon className="h-4 w-4 cursor-pointer" />
            </HoverCardTrigger>
            <HoverCardContent className="bg-modal-gradient rounded-lg border-modal-border-color">
              <button
                className="flex items-start gap-2 cursor-pointer"
                onClick={() => {
                  sendToBothAnalytics(
                    MIXPANEL_CLIENT_EVENT_NAMES.CLICK_ADVANCED_SETTINGS_ADMIN,
                    {
                      table_name: realTableName,
                      project_id: project?._id?.toString(),
                      enabled: !enableAdvanceSetting,
                    },
                    user?._id?.toString(),
                  )
                  setEnableAdvanceSetting(!enableAdvanceSetting)
                }}
                type="button"
              >
                <div className="text-center p-1.5">Advance Setting</div>
              </button>
            </HoverCardContent>
          </HoverCard>
        </div> */}
        <div className="ml-auto flex-end flex items-center gap-x-[20px]">
          <div className="flex gap-2">
            {selectedRowsIndex.size > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={bulkDeleteConfirm}
                disabled={isLoading}
                className="flex items-center gap-2 rounded-lg h-[40px] border-1 border-[rgba(255,85,55,1)] text-[rgba(255,85,55,1)] hover:text-[rgba(255,85,55,1)]"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                Delete Selected ({selectedRowsIndex.size})
              </Button>
            )}
          </div>
          {selectedTableColumn && (
            <DatabaseBatchActions
              tableName={tableName}
              csvTemplateData={csvTemplateData}
              onOk={handleBatchOk}
            >
              <Button
                variant="accent"
                size="custom"
                className="rounded-full h-10 px-base py-sm"
              >
                {tdb('batchActions.title')}
              </Button>
            </DatabaseBatchActions>
          )}
          <Button
            onClick={handleAddRecord}
            disabled={isLoading}
            className="bg-white text-[#222] rounded-lg h-[40px] hover:bg-white"
          >
            <Plus className="h-4 w-4 text-[#222]" />
            {t('add_new_record')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <Spin spinning={isLoading}>
      <div className="absolute inset-0 bottom-14 overflow-auto flex flex-col">
        {getTopView()}
        <DataTable
          columns={columns}
          data={dataSource}
          className="-mt-[0.5px] border-b dark:border-gray-800"
          tableHeadClassName="h-10 border-r dark:border-[#232323] border-[#232323] text-center bg-muted"
          tableCellClassName="py-3 border-r dark:border-[#232323] dark:text-gray-200 max-w-[200px] truncate text-center bg-background"
          onRowClick={handleRowClick}
          onRowSelectionChange={(selected) => {
            setSelectedRowsIndex(new Set(selected))
          }}
        />

        {/* <button
          type="button"
          onClick={handleAddRecord}
          className="flex items-center gap-2 py-3 text-[#15F1FD]"
        >
          <Plus className="h-4 w-4" /> {t('add_new_entry')}
        </button> */}

        {normalTablePagination?.total > 0 && (
          <PaginationView
            className="mt-6"
            pagination={normalTablePagination}
            onPageChange={onPageChange}
          />
        )}
      </div>
      <AlertDialog
        open={!!recordToDelete}
        onOpenChange={() => setRecordToDelete(null)}
      >
        <AlertDialogContent className="bg-modal-gradient rounded-lg border-modal-border-color">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Row?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this item? It will be removed from
              your data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent text-[#fff] opacity-60 hover:no-underline">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => recordToDelete && handleDelete(recordToDelete.id)}
              className="hover:bg-[#ff5537] hover:text-white rounded-lg bg-transparent border-1 border-[#ff5537] text-[#ff5537]"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Spin>
  )
}
